import { apiRequest } from "./client";
import { getApiBaseUrl } from "../lib/env";
import type {
  AuthUser,
  ChangePasswordRequest,
  LoginRequest,
  LoginResponse,
  LogoutRequest,
} from "@kkp/api-client";
import { AdminLoginParams, AuthTokens, LoginResult } from "../types/auth";

/**
 * Mock login API.
 *
 * Blueprint rule: UI should call src/api/* instead of fetch directly.
 * In mock mode, this resolves via mockFetch; when a backend exists, set
 * VITE_API_BASE_URL and this will POST to /auth/login.
 */
export async function login(body: AdminLoginParams): Promise<LoginResult> {
  const data = await apiRequest<LoginResponse, LoginRequest>({
    path: "/auth/login",
    // `body.username` is the admin login *credential* (sent as the email
    // identifier), not the user's display name.
    method: "POST",
    body: { email: body.username, password: body.password },
    auth: false,
  });

  return {
    tokens: {
      access: "cookie",
      refresh: "cookie",
    },
    user: data.user,
  };
}

/**
 * Logout API.
 * - Expects both access & refresh tokens (single-key storage).
 * - If API base is not configured, resolve via mock (no-op).
 * - If API base exists, POST to /auth/logout with refresh token.
 */
export async function logout(_tokens: AuthTokens): Promise<void> {
  const base = getApiBaseUrl();
  if (!base) {
    return;
  }
  await apiRequest<void, LogoutRequest>({
    path: "/auth/logout",
    method: "POST",
    body: {},
    auth: false,
  });
}

export async function getProfile(): Promise<AuthUser> {
  return apiRequest<AuthUser>({
    path: "/auth/me",
    method: "GET",
    allowRefresh: false,
  });
}

/**
 * Change the current admin user's password.
 *
 * On success the server revokes every existing session for the user and
 * issues a fresh cookie pair for the calling device so the requester stays
 * signed in. Other devices are signed out.
 */
export async function changePassword(
  body: ChangePasswordRequest,
): Promise<void> {
  await apiRequest<unknown, ChangePasswordRequest>({
    path: "/auth/change-password",
    method: "POST",
    body,
    allowRefresh: false,
  });
}
